// ---------------------------------------------------------------------------
// AI Audit Page — lists all AI interaction audit logs
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, type Column } from '../components/DataTable';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AiAuditEntry {
  id: string;
  request_id: string;
  request_type: string;
  input_hash: string;
  output_hash: string;
  model_version: string;
  latency_ms: number;
  operator_id: string | null;
  operator_action: string | null;
  created_at: string;
}

interface AiAuditDetailProps {
  entry: AiAuditEntry;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

const OPERATOR_ACTION_STYLES: Record<string, string> = {
  accepted: 'bg-primary-tint text-primary',
  modified: 'bg-warning-tint text-warning',
  rejected: 'bg-danger-tint text-danger',
};

// ---------------------------------------------------------------------------
// Detail panel
// ---------------------------------------------------------------------------

function AiAuditDetail({ entry, onClose }: AiAuditDetailProps) {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const requestTypeLabels = t('aiAudit.requestTypes', { returnObjects: true }) as Record<string, string>;
  return (
    <div className="bg-surface-raised border border-hairline rounded-sm shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-ink">
          {t('aiAudit.headings.detail')}
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="text-ink-muted hover:text-ink-muted"
          aria-label={t('aiAudit.detail.closeLabel')}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="font-medium text-ink-muted">{t('aiAudit.detail.requestId')}</span>
          <p className="mt-0.5 font-mono text-ink">{entry.request_id}</p>
        </div>
        <div>
          <span className="font-medium text-ink-muted">{t('aiAudit.detail.type')}</span>
          <p className="mt-0.5 text-ink">
            {requestTypeLabels[entry.request_type] ?? entry.request_type}
          </p>
        </div>
        <div>
          <span className="font-medium text-ink-muted">{t('aiAudit.detail.modelVersion')}</span>
          <p className="mt-0.5 text-ink">{entry.model_version}</p>
        </div>
        <div>
          <span className="font-medium text-ink-muted">{t('aiAudit.detail.latency')}</span>
          <p className="mt-0.5 text-ink">{t('aiAudit.detail.latencyValue', { ms: entry.latency_ms })}</p>
        </div>
        <div>
          <span className="font-medium text-ink-muted">{t('aiAudit.detail.operator')}</span>
          <p className="mt-0.5 text-ink">
            {entry.operator_id ?? t('common.notAvailable')}
          </p>
        </div>
        <div>
          <span className="font-medium text-ink-muted">{t('aiAudit.detail.operatorAction')}</span>
          <p className="mt-0.5">
            {entry.operator_action ? (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  OPERATOR_ACTION_STYLES[entry.operator_action] ??
                  'bg-surface-sunken text-ink'
                }`}
              >
                {entry.operator_action}
              </span>
            ) : (
              <span className="text-ink-muted">{t('aiAudit.detail.pending')}</span>
            )}
          </p>
        </div>
        <div>
          <span className="font-medium text-ink-muted">{t('aiAudit.detail.inputHash')}</span>
          <p className="mt-0.5 font-mono text-xs text-ink-muted truncate">
            {entry.input_hash}
          </p>
        </div>
        <div>
          <span className="font-medium text-ink-muted">{t('aiAudit.detail.outputHash')}</span>
          <p className="mt-0.5 font-mono text-xs text-ink-muted truncate">
            {entry.output_hash}
          </p>
        </div>
        <div className="col-span-2">
          <span className="font-medium text-ink-muted">{t('aiAudit.detail.timestamp')}</span>
          <p className="mt-0.5 text-ink tabular-nums font-mono">
            {f.date(entry.created_at, { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function AiAuditPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const requestTypeLabels = t('aiAudit.requestTypes', { returnObjects: true }) as Record<string, string>;
  const filterOptions = t('aiAudit.filters.options', { returnObjects: true }) as Record<string, string>;
  const columns = useMemo<Column<AiAuditEntry>[]>(
    () => [
      {
        key: 'createdAt',
        header: t('aiAudit.columns.timestamp'),
        render: (e) => (
          <span className="text-sm text-ink-muted font-mono whitespace-nowrap">
            {f.date(e.created_at, { dateStyle: 'medium', timeStyle: 'short' })}
          </span>
        ),
      },
      {
        key: 'requestType',
        header: t('aiAudit.columns.type'),
        render: (e) => (
          <span className="text-sm font-medium text-ink">
            {requestTypeLabels[e.request_type] ?? e.request_type}
          </span>
        ),
      },
      {
        key: 'operatorId',
        header: t('aiAudit.columns.operator'),
        render: (e) => (
          <span className="text-sm text-ink-muted">
            {e.operator_id ?? t('common.system')}
          </span>
        ),
      },
      {
        key: 'latencyMs',
        header: t('aiAudit.columns.latency'),
        render: (e) => (
          <span className="text-sm text-ink-muted">{t('aiAudit.detail.latencyValue', { ms: e.latency_ms })}</span>
        ),
      },
      {
        key: 'operatorAction',
        header: t('aiAudit.columns.action'),
        render: (e) =>
          e.operator_action ? (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                OPERATOR_ACTION_STYLES[e.operator_action] ??
                'bg-surface-sunken text-ink'
              }`}
            >
              {e.operator_action}
            </span>
          ) : (
            <span className="text-xs text-ink-muted">{t('aiAudit.detail.pending')}</span>
          ),
      },
    ],
    [f, t, requestTypeLabels],
  );
  const [entries, setEntries] = useState<AiAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<AiAuditEntry | null>(
    null,
  );

  // Filters
  const [requestTypeFilter, setRequestTypeFilter] = useState('');
  const [operatorActionFilter, setOperatorActionFilter] = useState('');

  const loadEntries = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (requestTypeFilter) params.set('request_type', requestTypeFilter);
    if (operatorActionFilter)
      params.set('operator_action', operatorActionFilter);

    const qs = params.toString();
    const url = `${API_BASE}/v1/admin/ai/audit-log${qs ? `?${qs}` : ''}`;

    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ data: AiAuditEntry[] }>;
      })
      .then((body) => setEntries(body.data))
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('aiAudit.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [requestTypeFilter, operatorActionFilter, t]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  if (loading) return <LoadingState rows={6} />;
  if (error) return <ErrorState message={error} onRetry={loadEntries} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl text-ink">{t('aiAudit.headings.title')}</h1>
          <p className="text-sm text-ink-muted mt-1">
            {t('aiAudit.headings.subtitle')}
          </p>
        </div>
      </div>

      {/* Detail panel */}
      {selectedEntry && (
        <AiAuditDetail
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
        />
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <div>
          <label
            htmlFor="ai-request-type-filter"
            className="block text-xs font-medium text-ink-muted mb-1"
          >
            {t('aiAudit.filters.requestType')}
          </label>
          <select
            id="ai-request-type-filter"
            value={requestTypeFilter}
            onChange={(e) => setRequestTypeFilter(e.target.value)}
            className="block w-48 rounded-sm border border-hairline-strong px-3 py-1.5 text-sm text-ink focus:border-primary focus:ring-1 focus:ring-primary"
          >
            <option value="">{filterOptions.all}</option>
            <option value="case_summary">{filterOptions.case_summary}</option>
            <option value="watchlist_triage">{filterOptions.watchlist_triage}</option>
            <option value="evidence_analysis">{filterOptions.evidence_analysis}</option>
            <option value="risk_assessment">{filterOptions.risk_assessment}</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="ai-action-filter"
            className="block text-xs font-medium text-ink-muted mb-1"
          >
            {t('aiAudit.filters.operatorAction')}
          </label>
          <select
            id="ai-action-filter"
            value={operatorActionFilter}
            onChange={(e) => setOperatorActionFilter(e.target.value)}
            className="block w-48 rounded-sm border border-hairline-strong px-3 py-1.5 text-sm text-ink focus:border-primary focus:ring-1 focus:ring-primary"
          >
            <option value="">{filterOptions.all}</option>
            <option value="accepted">{filterOptions.accepted}</option>
            <option value="modified">{filterOptions.modified}</option>
            <option value="rejected">{filterOptions.rejected}</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={loadEntries}
            className="px-4 py-1.5 text-sm font-medium bg-surface-sunken text-ink rounded-sm hover:bg-surface-sunken focus:outline-none focus:ring-2 focus:ring-ink-muted focus:ring-offset-1 transition-colors"
          >
            {t('common.applyFilters')}
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={entries}
        rowKey={(e) => e.id}
        onRowClick={(e) => setSelectedEntry(e)}
        emptyMessage={t('aiAudit.empty')}
        ariaLabel={t('aiAudit.ariaLabel')}
      />
    </div>
  );
}
